import { DEFAULT_PAYMENT_TERMS } from '../components/buyer_invoice_form/InvoicePaymentTermComponent';
import { TIME_IN } from '../components/supplier_invoice_form/LaborTimeSelect';
import { nullSafeGet, nullSafeGetOrElse } from './DataAccessUtils';
import { capitalize, roundToNearest } from './DataFormatterUtils';

export const LINE_ITEM_FIELD = {
	MISC: 'misc',
	TRAVEL: 'travel',
	FREIGHT: 'freight',
	INCURRED: 'incurredCost',
};

export const LINE_ITEM_FIELD_TO_DISPLAY_NAMES = {
	[LINE_ITEM_FIELD.MISC]: 'Miscellaneous',
	[LINE_ITEM_FIELD.TRAVEL]: 'Travel',
	[LINE_ITEM_FIELD.FREIGHT]: 'Freight',
	[LINE_ITEM_FIELD.INCURRED]: 'Incurred Cost',
};

export const getFixedFloatStringOrZero = (value) =>
	value ? `${parseFloat(value).toFixed(2)}` : '0';

export const getFixedFloatSum = (value1, value2) =>
	(parseFloat(value1) + parseFloat(value2)).toFixed(2);

export const getFixedFloatDifference = (value1, value2) =>
	(parseFloat(value1 || '0') - parseFloat(value2 || '0')).toFixed(2);

export const recalculateInvoiceTotalFields = (form) => {
	const subtotals = form.getFieldsValue([
		'laborTotalBeforeTax',
		'partsTotalBeforeTax',
		'equipmentTotalBeforeTax',
		'freightTotalBeforeTax',
		'travelTotalBeforeTax',
		'miscTotalBeforeTax',
		'invoiceTax',
	]);
	const invoiceTotalBeforeTax = (
		parseFloat(nullSafeGetOrElse('laborTotalBeforeTax', subtotals, 0)) +
		parseFloat(nullSafeGetOrElse('partsTotalBeforeTax', subtotals, 0)) +
		parseFloat(nullSafeGetOrElse('equipmentTotalBeforeTax', subtotals, 0)) +
		parseFloat(nullSafeGetOrElse('freightTotalBeforeTax', subtotals, 0)) +
		parseFloat(nullSafeGetOrElse('travelTotalBeforeTax', subtotals, 0)) +
		parseFloat(nullSafeGetOrElse('miscTotalBeforeTax', subtotals, 0))
	).toFixed(2);

	const invoiceTotalAfterTax = (
		parseFloat(invoiceTotalBeforeTax) + parseFloat(nullSafeGetOrElse('invoiceTax', subtotals, 0))
	).toFixed(2);

	const materialTotalBeforeTax = (
		parseFloat(nullSafeGetOrElse('partsTotalBeforeTax', subtotals, 0)) +
		parseFloat(nullSafeGetOrElse('equipmentTotalBeforeTax', subtotals, 0))
	).toFixed(2);

	form.setFieldsValue({
		invoiceTotalBeforeTax,
		invoiceTotalAfterTax,
		materialTotalBeforeTax,
	});
};

const getSimpleLineItems = (values, fieldName) =>
	(values[`${fieldName}Keys`] || []).map((key) => {
		const amount = values[`${fieldName}-${key}-amount`];
		return {
			description: values[`${fieldName}-${key}-description`],
			id: values[`${fieldName}-${key}-id`],
			unitPrice: values[`${fieldName}-${key}-unitPrice`]
				? `${values[`${fieldName}-${key}-unitPrice`]}`
				: undefined,
			amount: `${amount}`,
		};
	});

export const getQuoteInvoiceLineItems = (values, laborTimeIn) => {
	const laborItems = (values.laborKeys || []).map((laborKey) => {
		let laborItem = {
			roleType: values[`labor-${laborKey}-roleType`],
			numberOfTech: parseInt(values[`labor-${laborKey}-numberOfTech`], 10),
			seconds: roundToNearest(
				parseFloat(values[`labor-${laborKey}-time`]) *
					60 *
					(laborTimeIn === TIME_IN.MINUTES ? 1 : 60),
				1
			),
			hourlyRate: values[`labor-${laborKey}-hourlyRate`],
			amount: values[`labor-${laborKey}-amount`],
		};
		return laborItem;
	});

	const partsItems = (values.partsKeys || []).map((partsKey, i) => {
		let partAmt = values[`parts-${partsKey}-amount`];
		let partItem = {
			description: values[`parts-${partsKey}-description`],
			partId: values[`parts-${partsKey}-partId`] || i + 1,
			partNumber: values[`parts-${partsKey}-partNumber`],
			unitType: values[`parts-${partsKey}-unitType`],
			quantity: values[`parts-${partsKey}-quantity`]
				? `${values[`parts-${partsKey}-quantity`]}`
				: '',
			unitPrice: values[`parts-${partsKey}-unitPrice`]
				? `${values[`parts-${partsKey}-unitPrice`]}`
				: '',
			amount: `${partAmt}`,
		};
		return partItem;
	});

	const equipmentItems = (values.equipmentKeys || []).map((equipmentKey, i) => {
		let equipmentAmt = values[`equipment-${equipmentKey}-amount`];
		let equipmentItem = {
			description: values[`equipment-${equipmentKey}-description`],
			equipmentId: values[`equipment-${equipmentKey}-equipmentId`] || i + 1,
			equipmentNumber: values[`equipment-${equipmentKey}-equipmentNumber`],
			quantity: '1',
			unitType: 'each',
			unitPrice: values[`equipment-${equipmentKey}-unitPrice`]
				? `${values[`equipment-${equipmentKey}-unitPrice`]}`
				: undefined,
			amount: `${equipmentAmt}`,
		};
		return equipmentItem;
	});

	const miscItems = getSimpleLineItems(values, LINE_ITEM_FIELD.MISC);
	const freightItems = getSimpleLineItems(values, LINE_ITEM_FIELD.FREIGHT);
	const travelItems = getSimpleLineItems(values, LINE_ITEM_FIELD.TRAVEL);
	const incurredItems = getSimpleLineItems(values, LINE_ITEM_FIELD.INCURRED);

	const materialLineItems = partsItems.concat(equipmentItems);

	return {
		laborItems,
		miscItems,
		freightItems,
		travelItems,
		incurredItems,
		materialLineItems,
	};
};

export const getInvoiceEntityCommonValues = (values, formData, scope, currentUser, laborTimeIn) => {
	const { laborItems, miscItems, materialLineItems, travelItems, freightItems } =
		getQuoteInvoiceLineItems(values, laborTimeIn);

	const taxLineItems = (values.taxKeys || []).map((taxKey) => {
		return {
			taxType: `${values[`tax-${taxKey}-taxType`]}`,
			amount: `${values[`tax-${taxKey}-amount`]}`,
		};
	});

	return {
		id: values.id,
		scope,
		externalWorkOrderId: values.externalWorkOrderId,
		invoiceNumber: values.invoiceNumber,
		paymentTerms: values.paymentTerms,
		dueDate: values.dueDate,
		dateOfInvoice: values.dateOfInvoice,
		invoiceTaxRate: values.invoiceTaxRate,
		invoiceTax: getFixedFloatStringOrZero(values.invoiceTax),
		invoiceTotalBeforeTax: getFixedFloatStringOrZero(values.invoiceTotalBeforeTax),
		invoiceTotalAfterTax: getFixedFloatStringOrZero(values.invoiceTotalAfterTax),
		laborTotalBeforeTax: getFixedFloatStringOrZero(values.laborTotalBeforeTax),
		laborLineItems: laborItems || [],
		taxLineItems: taxLineItems || [],
		materialTotalBeforeTax: getFixedFloatStringOrZero(values.materialTotalBeforeTax),
		materialLineItems: materialLineItems || [],
		freightTotalBeforeTax: getFixedFloatStringOrZero(values.freightTotalBeforeTax),
		freightLineItems: freightItems || [],
		travelTotalBeforeTax: getFixedFloatStringOrZero(values.travelTotalBeforeTax),
		travelLineItems: travelItems || [],
		miscTotalBeforeTax: getFixedFloatStringOrZero(values.miscTotalBeforeTax),
		miscLineItems: miscItems || [],
		workOrderId: parseInt(formData.workOrderId, 10),
		createdBy: currentUser.email,
		attachments: values.attachments,
	};
};

export const getInvoiceDueDateBasedOnPaymentTerms = (getFieldValue, pt) => {
	const dateOfInvoice = getFieldValue('dateOfInvoice');

	if (!dateOfInvoice) return null;

	switch (pt) {
		case DEFAULT_PAYMENT_TERMS.NET_30:
			return dateOfInvoice.clone().add(30, 'days');
		case DEFAULT_PAYMENT_TERMS.NET_45:
			return dateOfInvoice.clone().add(45, 'days');
		case DEFAULT_PAYMENT_TERMS.NET_60:
			return dateOfInvoice.clone().add(60, 'days');
		case DEFAULT_PAYMENT_TERMS.NET_90:
			return dateOfInvoice.clone().add(90, 'days');
	}
};

const addSimpleLineItemsToFormData = (formData, fieldName) => {
	if (formData[`${fieldName}LineItems`] && formData[`${fieldName}LineItems`].length > 0) {
		const lineItems = formData[`${fieldName}LineItems`];
		formData[`${fieldName}Keys`] = lineItems.map((el, idx) => idx);
		lineItems.forEach((lineItem, idx) => {
			formData[`${fieldName}-${idx}-description`] = lineItem.description;
			formData[`${fieldName}-${idx}-unitPrice`] = lineItem.unitPrice;
			formData[`${fieldName}-${idx}-amount`] = lineItem.amount || lineItem.unitPrice || 0;
		});
		if (formData[`${fieldName}Keys`]) {
			formData[`${fieldName}TotalBeforeTax`] = formData[`${fieldName}Keys`]
				.reduce((acc, k2) => {
					const price = formData[`${fieldName}-${k2}-amount`] || 0;
					return getFixedFloatSum(acc, price);
				}, 0)
				.toString();
		}
	}
};

export const initialiseFormDataWithLineItems = (originalFormData, timeIn) => {
	const formData = JSON.parse(JSON.stringify(originalFormData));

	if (formData.laborLineItems) {
		formData.laborKeys = formData.laborLineItems.map((el, idx) => idx);
		formData.laborLineItems.forEach((lineItem, idx) => {
			formData[`labor-${idx}-roleType`] = lineItem.roleType;
			formData[`labor-${idx}-numberOfTech`] = lineItem.numberOfTech;
			formData[`labor-${idx}-time`] = parseFloat(
				`${lineItem.seconds / (timeIn === TIME_IN.MINUTES ? 60.0 : 3600.0)}`
			).toFixed(2);
			formData[`labor-${idx}-hourlyRate`] = lineItem.hourlyRate;
			formData[`labor-${idx}-amount`] =
				lineItem.amount || (lineItem.numberOfTech && lineItem.seconds && lineItem.hourlyRate)
					? (((lineItem.hourlyRate * lineItem.seconds) / 3600.0) * lineItem.numberOfTech).toString()
					: '0';
		});
		if (!formData[`laborTotalBeforeTax`]) {
			formData[`laborTotalBeforeTax`] = formData.laborKeys
				.reduce((acc, k) => {
					const price = formData[`labor-${k}-amount`] || 0;
					return getFixedFloatSum(acc, price);
				}, 0)
				.toString();
		}
	}

	const equipmentLineItems = nullSafeGetOrElse('materialLineItems', formData, []).filter(
		({ equipmentId }) => !!equipmentId
	);
	const partLineItems = nullSafeGetOrElse('materialLineItems', formData, []).filter(
		({ equipmentId }) => !equipmentId
	);

	if (nullSafeGetOrElse('materialLineItems', formData, []).length > 0) {
		formData.equipmentKeys = equipmentLineItems.map((_, idx) => idx);
		equipmentLineItems.forEach((lineItem, idx) => {
			formData[`equipment-${idx}-equipmentId`] = lineItem.equipmentId;
			formData[`equipment-${idx}-description`] = lineItem.description;
			formData[`equipment-${idx}-equipmentNumber`] = lineItem.equipmentNumber;
			formData[`equipment-${idx}-unitPrice`] = lineItem.unitPrice;
			formData[`equipment-${idx}-amount`] = lineItem.amount || lineItem.unitPrice || 0;
		});
		const equipmentTotal = formData.equipmentKeys.reduce(
			(acc, k) => getFixedFloatSum(acc, formData[`equipment-${k}-amount`] || 0),
			0
		);

		formData.partsKeys = partLineItems.map((_, idx) => idx);
		partLineItems.forEach((lineItem, idx) => {
			formData[`parts-${idx}-partId`] = lineItem.partId;
			formData[`parts-${idx}-description`] = lineItem.description;
			formData[`parts-${idx}-partNumber`] = lineItem.partNumber;
			formData[`parts-${idx}-unitType`] = lineItem.unitType;
			formData[`parts-${idx}-quantity`] = lineItem.quantity;
			formData[`parts-${idx}-unitPrice`] = lineItem.unitPrice;
			formData[`parts-${idx}-amount`] =
				lineItem.amount || lineItem.unitPrice * lineItem.quantity || 0;
		});
		const partsTotal = formData.partsKeys.reduce(
			(acc, k) => getFixedFloatSum(acc, formData[`parts-${k}-amount`] || 0),
			0
		);

		const materialTotal = parseFloat(formData.materialTotalBeforeTax) || 0;

		const { equipmentTotalBeforeTax, partsTotalBeforeTax } = getPartEquipmentTotalCostValues(
			equipmentLineItems,
			partLineItems,
			equipmentTotal,
			partsTotal,
			materialTotal
		);

		formData.equipmentTotalBeforeTax = equipmentTotalBeforeTax || 0;
		formData.partsTotalBeforeTax = partsTotalBeforeTax || 0;
	} else if (nullSafeGet('materialTotalBeforeTax', formData)) {
		formData[`partsTotalBeforeTax`] = formData.materialTotalBeforeTax;
	}

	addSimpleLineItemsToFormData(formData, LINE_ITEM_FIELD.MISC);
	addSimpleLineItemsToFormData(formData, LINE_ITEM_FIELD.TRAVEL);
	addSimpleLineItemsToFormData(formData, LINE_ITEM_FIELD.FREIGHT);
	addSimpleLineItemsToFormData(formData, LINE_ITEM_FIELD.INCURRED);

	if (formData.taxLineItems) {
		const taxLineItems = formData.taxLineItems;
		formData.taxKeys = taxLineItems.map((el, idx) => idx);
		taxLineItems.forEach((lineItem, idx) => {
			formData[`tax-${idx}-taxType`] = lineItem.taxType;
			formData[`tax-${idx}-amount`] = lineItem.amount;
		});
		if (!formData[`invoiceTax`]) {
			formData[`invoiceTax`] = formData.taxKeys
				.reduce((acc, k2) => {
					const price = formData[`tax-${k2}-amount`] || 0;
					return getFixedFloatSum(acc, price);
				}, 0)
				.toString();
		}
	}

	// Calculate invoiceTotalBeforeTax and invoiceTotalAfterTax if not already set
	// This ensures totals are calculated on initial load, not just when line items change
	if (!formData[`invoiceTotalBeforeTax`]) {
		const invoiceTotalBeforeTax = (
			parseFloat(nullSafeGetOrElse('laborTotalBeforeTax', formData, 0)) +
			parseFloat(nullSafeGetOrElse('partsTotalBeforeTax', formData, 0)) +
			parseFloat(nullSafeGetOrElse('equipmentTotalBeforeTax', formData, 0)) +
			parseFloat(nullSafeGetOrElse('freightTotalBeforeTax', formData, 0)) +
			parseFloat(nullSafeGetOrElse('travelTotalBeforeTax', formData, 0)) +
			parseFloat(nullSafeGetOrElse('miscTotalBeforeTax', formData, 0))
		).toFixed(2);
		formData[`invoiceTotalBeforeTax`] = invoiceTotalBeforeTax;
	}

	if (!formData[`invoiceTotalAfterTax`]) {
		const invoiceTotalAfterTax = (
			parseFloat(formData[`invoiceTotalBeforeTax`] || 0) +
			parseFloat(nullSafeGetOrElse('invoiceTax', formData, 0))
		).toFixed(2);
		formData[`invoiceTotalAfterTax`] = invoiceTotalAfterTax;
	}

	return {
		formData,
		labor: nullSafeGetOrElse('laborLineItems', formData, []).length,
		part: partLineItems.length,
		equipment: equipmentLineItems.length,
		misc: nullSafeGetOrElse(`${LINE_ITEM_FIELD.MISC}LineItems`, formData, []).length,
		travel: nullSafeGetOrElse(`${LINE_ITEM_FIELD.TRAVEL}LineItems`, formData, []).length,
		freight: nullSafeGetOrElse(`${LINE_ITEM_FIELD.FREIGHT}LineItems`, formData, []).length,
		incurred: nullSafeGetOrElse(`${LINE_ITEM_FIELD.INCURRED}LineItems`, formData, []).length,
		tax: nullSafeGetOrElse('taxLineItems', formData, []).length,
	};
};

const handleFirstAddSimpleLineItems = (state, setState, total, key, fieldName) =>
	setState(
		total
			? {
					[`${fieldName}ItemInitialValues`]: {
						...state[`${fieldName}ItemInitialValues`],
						[`${fieldName}-${key}-description`]: capitalize(
							LINE_ITEM_FIELD_TO_DISPLAY_NAMES[fieldName]
						),
						[`${fieldName}-${key}-unitPrice`]: total,
						[`${fieldName}-${key}-amount`]: total,
					},
			  }
			: {}
	);

export const handleFirstAddItem = (form, state, setState) => (keys, totalKeyName) => {
	if (keys.length === 1) {
		const total = form.getFieldValue(totalKeyName);
		const key = keys[0];
		switch (totalKeyName) {
			case 'laborTotalBeforeTax':
				setState(
					total
						? {
								laborItemInitialValues: {
									[`labor-${key}-numberOfTech`]: `${1}`,
									[`labor-${key}-time`]: `${1}`,
									[`labor-${key}-hourlyRate`]: `${total}`,
									[`labor-${key}-amount`]: `${total}`,
								},
						  }
						: {}
				);
				break;
			case 'partsTotalBeforeTax':
				setState(
					total
						? {
								partItemInitialValues: {
									[`parts-${key}-description`]: 'Part',
									[`parts-${key}-partNumber`]: '1',
									[`parts-${key}-quantity`]: 1,
									[`parts-${key}-unitPrice`]: total,
									[`parts-${key}-amount`]: total,
								},
						  }
						: {}
				);
				break;
			case 'equipmentTotalBeforeTax':
				setState(
					total
						? {
								equipmentItemInitialValues: {
									[`equipment-${key}-description`]: 'Equipment',
									[`equipment-${key}-equipmentNumber`]: '1',
									[`equipment-${key}-unitPrice`]: total,
									[`equipment-${key}-amount`]: total,
								},
						  }
						: {}
				);
				break;
			case `${LINE_ITEM_FIELD.MISC}TotalBeforeTax`:
				handleFirstAddSimpleLineItems(state, setState, total, key, LINE_ITEM_FIELD.MISC);
				break;
			case `${LINE_ITEM_FIELD.TRAVEL}TotalBeforeTax`:
				handleFirstAddSimpleLineItems(state, setState, total, key, LINE_ITEM_FIELD.TRAVEL);
				break;
			case `${LINE_ITEM_FIELD.FREIGHT}TotalBeforeTax`:
				handleFirstAddSimpleLineItems(state, setState, total, key, LINE_ITEM_FIELD.FREIGHT);
				break;
			case `${LINE_ITEM_FIELD.INCURRED}TotalBeforeTax`:
				handleFirstAddSimpleLineItems(state, setState, total, key, LINE_ITEM_FIELD.INCURRED);
				break;
		}
	}
};

export const addItem =
	(form, state, setState, existingKeys = undefined) =>
	(itemTypeVarName, uuid, totalKeyName) => {
		// can use data-binding to get
		const { getFieldValue, setFieldsValue } = form;
		const keys = getFieldValue(itemTypeVarName) || [];
		const nextKeys = keys.concat(uuid);
		// can use data-binding to set
		// important! notify form to detect changes
		setFieldsValue({
			[itemTypeVarName]: nextKeys,
		});
		handleFirstAddItem(form, state, setState)(nextKeys, totalKeyName);
		return uuid + 1;
	};

export const removeItem = (form, recalculateTotals) => (itemTypeCollectionKey, itemTypeKey, k) => {
	// can use data-binding to get
	const keys = form.getFieldValue(itemTypeCollectionKey);

	// can use data-binding to set
	form.setFieldsValue(
		{
			[itemTypeCollectionKey]: keys.filter((key) => key !== k),
		},
		() => {
			const itemKeys = form.getFieldValue(itemTypeCollectionKey);
			const itemTotalBeforeTax = itemKeys.reduce((acc, k2) => {
				const price = form.getFieldValue(`${itemTypeKey}-${k2}-amount`);
				return getFixedFloatSum(acc, price);
			}, 0);

			form.setFieldsValue(
				{ [`${itemTypeKey}TotalBeforeTax`]: itemTotalBeforeTax },
				recalculateTotals
			);
		}
	);
};

const getPartEquipmentTotalCostValues = (
	equipmentLineItems,
	partLineItems,
	equipmentTotal,
	partsTotal,
	materialTotal
) => {
	if (equipmentLineItems.length > 0 && partLineItems.length > 0) {
		return {
			equipmentTotalBeforeTax: equipmentTotal.toString(),
			partsTotalBeforeTax: partsTotal.toString(),
		};
	} else if (equipmentLineItems.length > 0) {
		return {
			equipmentTotalBeforeTax: equipmentTotal.toString(),
			partsTotalBeforeTax: getFixedFloatDifference(materialTotal, equipmentTotal).toString(),
		};
	} else if (partLineItems.length > 0) {
		return {
			equipmentTotalBeforeTax: getFixedFloatDifference(materialTotal, partsTotal).toString(),
			partsTotalBeforeTax: partsTotal.toString(),
		};
	} else return {};
};

const addSimpleLineItemsToInvoiceFromProposal =
	(form, state, setState, setFieldsValue) => (proposal, fieldName, uuid, copyFromFieldName?) => {
		let tempUuid = uuid;
		form.setFieldsValue({ [`${fieldName}Keys`]: [] });
		const totalLineItems = [
			...nullSafeGetOrElse(`${fieldName}LineItems`, proposal, []),
			...(copyFromFieldName
				? nullSafeGetOrElse(`${copyFromFieldName}LineItems`, proposal, [])
				: []),
		];
		totalLineItems.forEach((lineItem) => {
			const currentUUid = tempUuid;
			tempUuid = addItem(form, state, setState)(
				`${fieldName}Keys`,
				tempUuid,
				`${fieldName}TotalBeforeTax`
			);
			setFieldsValue({ [`${fieldName}-${currentUUid}-description`]: lineItem.description });
			setFieldsValue({ [`${fieldName}-${currentUUid}-unitPrice`]: lineItem.amount });
			setFieldsValue({
				[`${fieldName}-${currentUUid}-amount`]: lineItem.amount || lineItem.unitPrice || 0,
			});
		});

		return tempUuid;
	};

export const transferToQuoteFromProposal = (form, state, setState) => (formData, proposal) => {
	const materialTotalBeforeTax = nullSafeGetOrElse('materialTotalBeforeTax', proposal, 0);
	const laborTotalBeforeTax = nullSafeGetOrElse('laborTotalBeforeTax', proposal, 0);
	const freightTotalBeforeTax = nullSafeGetOrElse('freightTotalBeforeTax', proposal, 0);
	const miscTotalBeforeTax = nullSafeGetOrElse('miscTotalBeforeTax', proposal, 0);
	const travelTotalBeforeTax = nullSafeGetOrElse('travelTotalBeforeTax', proposal, 0);
	const incurredCostTotalBeforeTax = nullSafeGetOrElse('incurredCostTotalBeforeTax', proposal, 0);
	const invoiceTax = nullSafeGetOrElse('tax', proposal, 0);
	const scope = nullSafeGetOrElse('scope', proposal, '');

	const { setFieldsValue, getFieldValue } = form;

	setFieldsValue({ [`scope`]: scope });

	let equipmentUuid = 1;
	let partUuid = 1;
	let laborUuid = 1;
	let miscUuid = 1;
	let travelUuid = 1;
	let freightUuid = 1;

	if (
		materialTotalBeforeTax !== 0 &&
		nullSafeGetOrElse('materialLineItems', proposal, []).length > 0
	) {
		const equipmentLineItems = nullSafeGetOrElse('materialLineItems', proposal, []).filter(
			({ partId, partNumber }) => !partId && !partNumber
		);

		equipmentLineItems.forEach((lineItem) => {
			const currentUUid = equipmentUuid;
			equipmentUuid = addItem(form, state, setState)(
				'equipmentKeys',
				equipmentUuid,
				'equipmentTotalBeforeTax'
			);

			setFieldsValue({ [`equipment-${currentUUid}-description`]: lineItem.description });
			setFieldsValue({
				[`equipment-${currentUUid}-equipmentNumber`]:
					lineItem.equipmentNumber || lineItem.equipmentId,
			});
			setFieldsValue({ [`equipment-${currentUUid}-unitPrice`]: lineItem.unitPrice });
			setFieldsValue({
				[`equipment-${currentUUid}-amount`]: lineItem.amount || lineItem.unitPrice || 0,
			});
		});

		const equipmentTotal = (getFieldValue('equipmentKeys') || []).reduce(
			(acc, k) => getFixedFloatSum(acc, getFieldValue(`equipment-${k}-amount`) || 0),
			0
		);

		const partLineItems = nullSafeGetOrElse('materialLineItems', proposal, []).filter(
			({ partId, partNumber }) => partId || partNumber
		);

		partLineItems.forEach((lineItem) => {
			const currentUUid = partUuid;
			partUuid = addItem(form, state, setState)('partsKeys', partUuid, 'partsTotalBeforeTax');

			setFieldsValue({ [`parts-${currentUUid}-description`]: lineItem.description });
			setFieldsValue({
				[`parts-${currentUUid}-partNumber`]:
					lineItem.partNumber ||
					(lineItem.partId && nullSafeGetOrElse('partId', lineItem, '').toString()),
			});
			setFieldsValue({ [`parts-${currentUUid}-unitType`]: lineItem.unitType });
			setFieldsValue({ [`parts-${currentUUid}-quantity`]: lineItem.quantity });
			setFieldsValue({ [`parts-${currentUUid}-unitPrice`]: lineItem.unitPrice });
			setFieldsValue({
				[`parts-${currentUUid}-amount`]:
					lineItem.amount || lineItem.unitPrice * lineItem.quantity || 0,
			});
		});
		const partsTotal = (getFieldValue('partsKeys') || []).reduce(
			(acc, k) => getFixedFloatSum(acc, getFieldValue(`parts-${k}-amount`) || 0),
			0
		);

		const materialTotal = parseFloat(proposal.materialTotalBeforeTax) || 0;

		const { equipmentTotalBeforeTax, partsTotalBeforeTax } = getPartEquipmentTotalCostValues(
			equipmentLineItems,
			partLineItems,
			equipmentTotal,
			partsTotal,
			materialTotal
		);

		setFieldsValue({
			...(equipmentTotalBeforeTax && { equipmentTotalBeforeTax }),
			...(partsTotalBeforeTax && { partsTotalBeforeTax }),
		});
	} else if (nullSafeGet('materialTotalBeforeTax', proposal)) {
		setFieldsValue({ partsTotalBeforeTax: proposal.materialTotalBeforeTax });
	}

	setFieldsValue({ [`laborKeys`]: [] });
	nullSafeGetOrElse('laborLineItems', proposal, []).forEach((lineItem) => {
		const currentUUid = laborUuid;
		laborUuid = addItem(form, state, setState)('laborKeys', laborUuid, 'laborTotalBeforeTax');
		setFieldsValue({ [`labor-${currentUUid}-roleType`]: lineItem.roleType });
		setFieldsValue({ [`labor-${currentUUid}-numberOfTech`]: lineItem.numberOfTech });
		setFieldsValue({ [`labor-${currentUUid}-hourlyRate`]: lineItem.hourlyRate });
		setFieldsValue({
			[`labor-${currentUUid}-time`]: parseFloat(
				`${lineItem.seconds / (state.timeIn === TIME_IN.MINUTES ? 60.0 : 3600.0)}`
			).toFixed(2),
		});
		setFieldsValue({
			[`labor-${currentUUid}-amount`]:
				lineItem.amount || (lineItem.numberOfTech && lineItem.seconds && lineItem.hourlyRate)
					? (((lineItem.hourlyRate * lineItem.seconds) / 3600.0) * lineItem.numberOfTech).toString()
					: '0',
		});
	});
	setFieldsValue({ laborTotalBeforeTax: laborTotalBeforeTax.toString() });

	miscUuid = addSimpleLineItemsToInvoiceFromProposal(form, state, setState, setFieldsValue)(
		proposal,
		LINE_ITEM_FIELD.MISC,
		miscUuid,
		LINE_ITEM_FIELD.INCURRED
	);

	if (
		!nullSafeGet(`${LINE_ITEM_FIELD.INCURRED}LineItems.0`, proposal) &&
		parseFloat(incurredCostTotalBeforeTax) !== 0
	) {
		const currentUUid = miscUuid;
		miscUuid = addItem(form, state, setState)('miscKeys', miscUuid, 'miscTotalBeforeTax');
		setFieldsValue({ [`misc-${currentUUid}-amount`]: incurredCostTotalBeforeTax.toString() });
		setFieldsValue({
			[`misc-${currentUUid}-unitPrice`]: incurredCostTotalBeforeTax.toString(),
		});
		setFieldsValue({ [`misc-${currentUUid}-description`]: 'Incurred Cost' });
	}

	setFieldsValue({
		miscTotalBeforeTax: (
			parseFloat(miscTotalBeforeTax) + parseFloat(incurredCostTotalBeforeTax)
		).toString(),
	});

	freightUuid = addSimpleLineItemsToInvoiceFromProposal(
		form,
		state,
		setState,
		setFieldsValue
	)(proposal, LINE_ITEM_FIELD.FREIGHT, freightUuid);
	form.setFieldsValue({ freightTotalBeforeTax: freightTotalBeforeTax.toString() });

	travelUuid = addSimpleLineItemsToInvoiceFromProposal(
		form,
		state,
		setState,
		setFieldsValue
	)(proposal, LINE_ITEM_FIELD.TRAVEL, travelUuid);
	form.setFieldsValue({ travelTotalBeforeTax: travelTotalBeforeTax.toString() });
	form.setFieldsValue({ invoiceTax: invoiceTax });

	return {
		part: partUuid,
		equipment: equipmentUuid,
		labor: laborUuid,
		misc: miscUuid,
		freight: freightUuid,
		travel: travelUuid,
	};
};

export const initializeLineItemKeys = (formData, getFieldDecorator) => {
	getFieldDecorator('partsKeys', { initialValue: formData.partsKeys || [] });
	getFieldDecorator('equipmentKeys', { initialValue: formData.equipmentKeys || [] });
	getFieldDecorator(`${LINE_ITEM_FIELD.MISC}Keys`, {
		initialValue: formData[`${LINE_ITEM_FIELD.MISC}Keys`] || [],
	});
	getFieldDecorator(`${LINE_ITEM_FIELD.TRAVEL}Keys`, {
		initialValue: formData[`${LINE_ITEM_FIELD.TRAVEL}Keys`] || [],
	});
	getFieldDecorator(`${LINE_ITEM_FIELD.FREIGHT}Keys`, {
		initialValue: formData[`${LINE_ITEM_FIELD.FREIGHT}Keys`] || [],
	});
	getFieldDecorator(`${LINE_ITEM_FIELD.INCURRED}Keys`, {
		initialValue: formData[`${LINE_ITEM_FIELD.INCURRED}Keys`] || [],
	});
	getFieldDecorator('laborKeys', { initialValue: formData.laborKeys || [] });
	getFieldDecorator('taxKeys', { initialValue: formData.taxKeys || [] });
};

export const initialiseQuoteInvoiceCommonTotals = (formData, getFieldDecorator) => {
	getFieldDecorator(`materialTotalBeforeTax`, {
		initialValue: nullSafeGet(`materialTotalBeforeTax`, formData),
	});
	getFieldDecorator(`partsTotalBeforeTax`, {
		initialValue: nullSafeGet(`partsTotalBeforeTax`, formData),
	});

	getFieldDecorator(`laborTotalBeforeTax`, {
		initialValue: nullSafeGet(`laborTotalBeforeTax`, formData),
	});
	getFieldDecorator(`equipmentTotalBeforeTax`, {
		initialValue: nullSafeGet(`equipmentTotalBeforeTax`, formData),
	});
};

export const initializeInvoiceTotalFields = (formData, getFieldDecorator) => {
	getFieldDecorator(`invoiceTotalAfterTax`, {
		initialValue: nullSafeGet(`invoiceTotalAfterTax`, formData),
	});
	getFieldDecorator(`invoiceTotalBeforeTax`, {
		initialValue: nullSafeGet(`invoiceTotalBeforeTax`, formData),
	});

	initialiseQuoteInvoiceCommonTotals(formData, getFieldDecorator);
};

export const mergeProposalIntoFormData = (formData, proposal) => {
	const incurredCostLineItems =
		nullSafeGetOrElse('incurredCostLineItems', proposal, formData.incurredCostLineItems) || [];
	const incurredCostTotalBeforeTax = nullSafeGetOrElse(
		'incurredCostTotalBeforeTax',
		proposal,
		formData.incurredCostTotalBeforeTax
	);
	const miscLineItems = nullSafeGetOrElse('miscLineItems', proposal, formData.miscLineItems) || [];
	const miscTotalBeforeTax =
		nullSafeGetOrElse('miscTotalBeforeTax', proposal, formData.miscTotalBeforeTax) || 0;
	const materialLineItems =
		nullSafeGetOrElse('materialLineItems', proposal, formData.materialLineItems) || [];
	const materialTotalBeforeTax = nullSafeGetOrElse(
		'materialTotalBeforeTax',
		proposal,
		formData.materialTotalBeforeTax
	);
	const tax = nullSafeGetOrElse('tax', proposal, formData.tax);

	let finalMiscLineItems = [...miscLineItems, ...incurredCostLineItems];
	let finalMiscTotalBeforeTax = parseFloat(miscTotalBeforeTax) || 0;

	// Handle special case: if there's miscTotalBeforeTax but no miscLineItems,
	// create a misc item for it (similar to incurred costs)
	if (miscLineItems.length === 0 && parseFloat(miscTotalBeforeTax) !== 0) {
		finalMiscLineItems = [
			...finalMiscLineItems,
			{
				description: 'Miscellaneous',
				amount: miscTotalBeforeTax.toString(),
				unitPrice: miscTotalBeforeTax.toString(),
			},
		];
	}

	// Handle special case: if there's incurredCostTotalBeforeTax but no incurredCostLineItems/
	if (incurredCostLineItems.length === 0 && parseFloat(incurredCostTotalBeforeTax) !== 0) {
		finalMiscLineItems = [
			...finalMiscLineItems,
			{
				description: 'Incurred Cost',
				amount: incurredCostTotalBeforeTax.toString(),
				unitPrice: incurredCostTotalBeforeTax.toString(),
			},
		];
	}

	// Add incurredCostTotalBeforeTax to miscTotalBeforeTax
	if (parseFloat(incurredCostTotalBeforeTax) !== 0) {
		finalMiscTotalBeforeTax =
			parseFloat(miscTotalBeforeTax) + parseFloat(incurredCostTotalBeforeTax);
	}

	let partsTotalBeforeTax = nullSafeGetOrElse('partsTotalBeforeTax', formData, null);
	if (
		(!materialLineItems || materialLineItems.length === 0) &&
		materialTotalBeforeTax &&
		parseFloat(materialTotalBeforeTax) !== 0
	) {
		partsTotalBeforeTax = materialTotalBeforeTax;
	}

	return {
		...formData,
		scope: nullSafeGetOrElse('scope', proposal, formData.scope),
		laborLineItems: nullSafeGetOrElse('laborLineItems', proposal, formData.laborLineItems),
		materialLineItems: materialLineItems,
		miscLineItems: finalMiscLineItems,
		freightLineItems: nullSafeGetOrElse('freightLineItems', proposal, formData.freightLineItems),
		travelLineItems: nullSafeGetOrElse('travelLineItems', proposal, formData.travelLineItems),
		incurredCostLineItems: incurredCostLineItems,
		laborTotalBeforeTax: nullSafeGetOrElse(
			'laborTotalBeforeTax',
			proposal,
			formData.laborTotalBeforeTax
		),
		materialTotalBeforeTax: materialTotalBeforeTax,
		partsTotalBeforeTax:
			partsTotalBeforeTax !== null
				? partsTotalBeforeTax
				: nullSafeGetOrElse('partsTotalBeforeTax', formData, null),
		miscTotalBeforeTax: finalMiscTotalBeforeTax.toString(),
		freightTotalBeforeTax: nullSafeGetOrElse(
			'freightTotalBeforeTax',
			proposal,
			formData.freightTotalBeforeTax
		),
		travelTotalBeforeTax: nullSafeGetOrElse(
			'travelTotalBeforeTax',
			proposal,
			formData.travelTotalBeforeTax
		),
		incurredCostTotalBeforeTax: incurredCostTotalBeforeTax,
		tax: tax,
		invoiceTax: tax,
	};
};

// Utility function to compare parsed data with final submitted data
export const compareParsedWithFinal = (parsedSnapshot, finalInvoice) => {
	if (!parsedSnapshot) {
		return null;
	}

	const changedFields = [];
	let totalFieldsChanged = 0;

	// Compare top-level fields (normalize values for comparison)
	const normalizeValue = (val) => {
		if (val === null || val === undefined) return '';
		if (typeof val === 'number') return val.toString();
		if (val instanceof Date) return val.toISOString();
		return String(val).trim();
	};

	const fieldsToCompare = ['invoiceNumber', 'invoiceTax', 'invoiceTotalAfterTax', 'paymentTerms'];

	fieldsToCompare.forEach((field) => {
		const parsedVal = normalizeValue(parsedSnapshot[field]);
		const finalVal = normalizeValue(finalInvoice[field]);
		if (parsedVal !== finalVal) {
			changedFields.push(field);
			totalFieldsChanged++;
		}
	});

	// Compare line items (minimal - just count changes)
	const getLineItemCount = (items) => (items && Array.isArray(items) ? items.length : 0);

	const laborParsed = getLineItemCount(parsedSnapshot.laborLineItems);
	const laborFinal = getLineItemCount(finalInvoice.laborLineItems);
	const laborChanged = Math.abs(laborParsed - laborFinal);

	const materialParsed = getLineItemCount(parsedSnapshot.materialLineItems);
	const materialFinal = getLineItemCount(finalInvoice.materialLineItems);
	const materialChanged = Math.abs(materialParsed - materialFinal);

	const travelParsed = getLineItemCount(parsedSnapshot.travelLineItems);
	const travelFinal = getLineItemCount(finalInvoice.travelLineItems);
	const travelChanged = Math.abs(travelParsed - travelFinal);

	const freightParsed = getLineItemCount(parsedSnapshot.freightLineItems);
	const freightFinal = getLineItemCount(finalInvoice.freightLineItems);
	const freightChanged = Math.abs(freightParsed - freightFinal);

	const miscParsed = getLineItemCount(parsedSnapshot.miscLineItems);
	const miscFinal = getLineItemCount(finalInvoice.miscLineItems);
	const miscChanged = Math.abs(miscParsed - miscFinal);

	const totalLineItemsChanged =
		laborChanged + materialChanged + travelChanged + freightChanged + miscChanged;

	return {
		total_fields_changed: totalFieldsChanged,
		changed_fields: changedFields,
		labor_line_items_changed: laborChanged,
		material_line_items_changed: materialChanged,
		travel_line_items_changed: travelChanged,
		freight_line_items_changed: freightChanged,
		misc_line_items_changed: miscChanged,
		total_line_items_changed: totalLineItemsChanged,
	};
};
